import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, LinearProgress, IconButton, Button, Dialog, DialogTitle, DialogContent, Paper, Typography, CircularProgress, Tooltip, Menu, MenuItem, Tab, Tabs, FormControl, Select, useMediaQuery, useTheme, TextField, Autocomplete, Grid } from '@mui/material';
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { DataGridPro, GridFilterModel, GridSortModel, useGridApiRef } from "@mui/x-data-grid-pro";
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { GridColDef, GridFilterItem, GridToolbarFilterButton, GridToolbarContainer, 
    getGridStringOperators, GridToolbarColumnsButton, GridToolbarDensitySelector
} from '@mui/x-data-grid';
import { RiExpandUpDownLine } from "react-icons/ri";
import { FaArchive, FaDownload, FaPaperPlane, FaPlus, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import useAuth from '../context/AuthProvider';
import { apiClient } from '../helper/api';
import { writeXlsxFile, formatUtcToLocal } from '../helper/utils';
import { documentTypeToStateCode, stateCodeToDocumentType, STAMPED_STATE_CODES } from '../helper/stampedDocumentState';
import { AddStampedDocument } from '../components/AddEdit/AddStampedDocument';
import moment from 'moment';
import { DarkBlueBtnColor, GreenBtnColor, LightBlueBtnColor, OrangeBtnColor, PrimaryBlueColor, WhiteColor } from '../helper/theme';
import { HoverUnselectedLightBlueBtnColor } from '../helper/theme';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import { DashboardButton } from '../components/Elements/DashboardTile';
import { Search, Clear, Refresh } from '@mui/icons-material';


export interface StampedDocumentRow {
    id: number;    // stampedDocumentDetailId
    type: string;  // documentType
    client: string;
    partnersId: number;
    sentOn: string;
    sentBy: string;
};
export interface StampedDocument {
    stampedDocumentId: number;
    documentName: string;
    blobAddress: string;
    fileSizeKB: string;
    sentOn: string;
};
export interface DocumentDetail {
    stampedDocumentDetailId: number;
    documentType: string;
    userFirstName: string;
    client: string; 
    partnersId: number;
    documents: StampedDocument[];
    isArchived: boolean;
};
export interface StampedDocumentApiResponse {
    totalCount: number;
    currentPage: number;
    totalPages: number;
    documentDetails: DocumentDetail[];
};


const ClientUserRoles: string[] = [
    "Customer User",
    "Client Admin",
    "Client Processors",
    "State Reporter",
];

export enum TabNames {
    Open = 'OPEN',
    Archive = 'ARCHIVE',
};

/** Synthetic filter rows from the queue bar — strip by id only so column filters on the same fields still persist. */
const QUEUE_BAR_FILTER_ITEM_IDS = new Set(['queue-bar-client', 'queue-bar-state', 'queue-bar-sentby']);

interface StampedDocPartnerOption {
    partnersId: number;
    customerName: string;
}

function buildQueueBarFilterItems(
    partners: StampedDocPartnerOption[],
    clientPartnersId: number,
    stateType: string,
    sentBy: string
): GridFilterItem[] {
    const items: GridFilterItem[] = [];
    if (clientPartnersId > 0) {
        const name = partners.find((p) => p.partnersId === clientPartnersId)?.customerName?.trim();
        if (name) {
            items.push({ id: 'queue-bar-client', columnField: 'client', operatorValue: 'equals', value: name });
        }
    }
    if (stateType.trim()) {
        const docType = stateCodeToDocumentType(stateType.trim());
        if (docType) {
            // Show NY/IL in the grid filter UI; API still receives full document type via normalizeStampedFilterModelForApi.
            items.push({
                id: 'queue-bar-state',
                columnField: 'type',
                operatorValue: 'equals',
                value: stateType.trim().toUpperCase(),
            });
        }
    }
    if (sentBy.trim()) {
        items.push({ id: 'queue-bar-sentby', columnField: 'sentBy', operatorValue: 'contains', value: sentBy.trim() });
    }
    return items;
}

/** Stable fingerprint for filter items so effects do not re-run when only array references change. */
function stampedFilterItemsKey(items: GridFilterItem[]): string {
    return JSON.stringify(
        items.map((i) => ({
            f: i.columnField,
            o: i.operatorValue ?? '',
            v: i.value,
        }))
    );
}

function normalizeStampedFilterModelForApi(model: GridFilterModel): GridFilterModel {
    return {
        ...model,
        items: model.items.map((item) => {
            if (item.columnField !== 'type') return item;
            const raw = item.value;
            if (raw == null || typeof raw !== 'string') return item;
            const mapped = stateCodeToDocumentType(raw.trim());
            if (mapped) return { ...item, value: mapped };
            return item;
        }),
    };
}

export const SendStampedDocument: React.FC = () => {
    const { user } = useAuth();
    const token = user?.token;
    const IsUserClient = Boolean(ClientUserRoles.some(role => user?.userRoles?.includes(role)));
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(25);
    const [totalCount, setTotalCount] = useState(0);
    const [selectedRows, setSelectedRows] = useState<number[]>([]);
    const [addStampedDocumentOpen, setAddStampedDocumentOpen] = useState(false);
    const [viewAttachmentsOpen, setViewAttachmentsOpen] = useState(false);
    const [selectedDocumentDetail, setSelectedDocumentDetail] = useState<DocumentDetail | null>(null);
    const [documentDetailsData, setDocumentDetailsData] = useState<DocumentDetail[]>([]);
    const [documentViewerOpen, setDocumentViewerOpen] = useState(false);
    const [documentViewerUrl, setDocumentViewerUrl] = useState<string | null>(null);
    const [documentViewerFileName, setDocumentViewerFileName] = useState<string | null>(null);
    const [documentViewerBlobAddress, setDocumentViewerBlobAddress] = useState<string | null>(null);
    const [documentLoading, setDocumentLoading] = useState(false);
    const [sortField, setSortField] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<string>('desc');
    /** Filters applied from the DataGrid column filter UI only (excludes queue bar fields). */
    const [gridFilterItems, setGridFilterItems] = useState<GridFilterItem[]>([]);
    const [filterLinkOperator, setFilterLinkOperator] = useState<GridFilterModel['linkOperator']>(undefined);
    const [queuePartners, setQueuePartners] = useState<StampedDocPartnerOption[]>([]);
    const [queueFilterClientId, setQueueFilterClientId] = useState<number>(0);
    const [queueFilterStateType, setQueueFilterStateType] = useState<string>('');
    const [queueFilterSentBy, setQueueFilterSentBy] = useState<string>('');
    const [draftQueueFilterClientId, setDraftQueueFilterClientId] = useState<number>(0);
    const [draftQueueFilterStateType, setDraftQueueFilterStateType] = useState<string>('');
    const [draftQueueFilterSentBy, setDraftQueueFilterSentBy] = useState<string>('');
    const stampedFilterPartnerOptions = useMemo(
        () =>
            queuePartners
                .filter((p) => Number(p.partnersId) > 0 && (p.customerName || '').trim() !== '')
                .sort((a, b) => a.customerName.localeCompare(b.customerName)),
        [queuePartners]
    );
    const stampedFilterStateOptions = useMemo(() => [...STAMPED_STATE_CODES], []);

    const filterModel: GridFilterModel = useMemo(() => ({
        items: [
            ...gridFilterItems,
            ...buildQueueBarFilterItems(queuePartners, queueFilterClientId, queueFilterStateType, queueFilterSentBy),
        ],
        linkOperator: filterLinkOperator,
    }), [gridFilterItems, filterLinkOperator, queuePartners, queueFilterClientId, queueFilterStateType, queueFilterSentBy]);

    /** Value-based key for fetch effect — avoids infinite loops from `filterModel` object identity + DataGrid `onFilterModelChange` churn. */
    const filterFetchKey = useMemo(() => {
        const barItems = buildQueueBarFilterItems(
            queuePartners,
            queueFilterClientId,
            queueFilterStateType,
            queueFilterSentBy
        );
        return JSON.stringify({
            grid: stampedFilterItemsKey(gridFilterItems),
            link: filterLinkOperator ?? null,
            bar: stampedFilterItemsKey(barItems),
        });
    }, [
        gridFilterItems,
        filterLinkOperator,
        queuePartners,
        queueFilterClientId,
        queueFilterStateType,
        queueFilterSentBy,
    ]);

    const theme = useTheme();
    const isTabletOrDown = useMediaQuery(theme.breakpoints.down('md'));
    const sendStampedDocumentGridApiRef = useGridApiRef();

    const [dataLoaded, setDataLoaded] = React.useState(false);
    const [isLoading, setIsLoading] = React.useState(true);

    // tab state
    const [selectedTab, setSelectedTab] = useState<TabNames>(TabNames.Open);
    const handleTabChange = (tab: TabNames) => {
        if (tab !== selectedTab) {
            setSelectedTab(tab);
            setCurrentPage(0); // Reset to first page when tab changes
        }
    };

    const filterOperators = getGridStringOperators();

    const [rows, setRows] = useState<StampedDocumentRow[]>([]);

    const columns: GridColDef[] = [
        { field: 'type', headerName: 'State', 
            type: 'string', 
            flex: 0.62, minWidth: 132,
            align: 'left', headerAlign: 'left', 
            hideSortIcons: true, filterOperators,
            renderCell: (params) => documentTypeToStateCode(String(params.row.type || '')) || '—',
            renderHeader: (params) => {
                return (
                    <div>
                        
                        {sortField === 'type' && sortDirection === 'asc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span>
                        </>)}
                        {sortField === 'type' && sortDirection === 'desc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span>
                        </>)}
                        {sortField !== 'type' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <RiExpandUpDownLine style={{ fontSize: '16px', color: '#999999'}} />
                        </>)}
                    </div>
                )
            }, 
        },
        { field: 'client', headerName: 'Client', 
            type: 'string', 
            flex: 1.12, minWidth: 168, 
            align: 'left', headerAlign: 'left', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => {
                return (
                    <div>
                        
                        {sortField === 'client' && sortDirection === 'asc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span>
                        </>)}
                        {sortField === 'client' && sortDirection === 'desc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span>
                        </>)}
                        {sortField !== 'client' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <RiExpandUpDownLine style={{ fontSize: '16px', color: '#999999'}} />
                        </>)}
                    </div>
                )
            }, 
        },
        { field: 'sentOn', headerName: 'Sent On', 
            type: 'date',
            flex: 0.62, minWidth: 132, 
            align: 'left', headerAlign: 'left', 
            hideSortIcons: true, filterOperators, 
            renderHeader: (params) => {
                return (
                    <div>
                        
                        {sortField === 'sentOn' && sortDirection === 'asc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span>
                        </>)}
                        {sortField === 'sentOn' && sortDirection === 'desc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span>
                        </>)}
                        {sortField !== 'sentOn' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <RiExpandUpDownLine style={{ fontSize: '16px', color: '#999999'}} />
                        </>)}
                    </div>
                )
            }, 
            valueGetter: (params) => {
                if (!params.value) return null;
                return moment.utc(params.value).toDate();
            },
            renderCell: (params) => formatUtcToLocal(params.value, 'MM/DD/YYYY'),
        },
        { field: 'sentBy', headerName: 'Sent By', 
            type: 'string', 
            flex: 0.48, minWidth: 80, 
            align: 'left', headerAlign: 'left', 
            hideSortIcons: true, filterOperators,
            renderHeader: (params) => {
                return (
                    <div>
                        
                        {sortField === 'sentBy' && sortDirection === 'asc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px', color: '#999999'}} /></span>
                        </>)}
                        {sortField === 'sentBy' && sortDirection === 'desc' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <span className="d-inline-flex flex-column table-card-title"><KeyboardArrowUp style={{ fontSize: '16px', color: '#999999'}} /> <KeyboardArrowDown style={{ fontSize: '16px', marginTop: '-9px'}} /></span>
                        </>)}
                        {sortField !== 'sentBy' && (<>
                            <span className="table-card-title">{params.colDef.headerName}</span>
                            <RiExpandUpDownLine style={{ fontSize: '16px', color: '#999999'}} />
                        </>)}
                    </div>
                )
            }, 
        },
        { field: 'action', headerName: 'Action', 
            flex: 0.48, minWidth: 100, 
            align: 'center', headerAlign: 'center', 
            filterable: false, 
            sortable: false,
            disableColumnMenu: true,
            disableExport: true,
            hideSortIcons: true,
            renderHeader: (params) => (
                <div>
                    <span className="table-card-title">{params.colDef.headerName}</span>
                    <RiExpandUpDownLine />
                </div>
            ),
            renderCell: (params) => (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    width: '100%',
                    gap: '8px',
                }}>
                    <IconButton title="View" 
                        onClick={(e) => {
                            e.stopPropagation();
                            handleViewAttachments(params.row.id);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                    >
                        <Tooltip title="View" arrow={true} placement="top">
                            <RemoveRedEyeOutlinedIcon fontSize="small" />
                        </Tooltip>
                        </IconButton>
                    <IconButton title="Download"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadRow(params.row.id);
                        }}
                        sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                    >
                        <Tooltip title="Download" arrow={true} placement="top">
                            <FileDownloadOutlinedIcon fontSize="small" />
                        </Tooltip>
                    </IconButton>
                </div>
            )
        }
    ];

    const handleNewSubmission = () => {
        setAddStampedDocumentOpen(true);
    };

    const handleCloseAddStampedDocument = () => {
        setAddStampedDocumentOpen(false);
    };

    const handleStampedDocumentSuccess = () => {
        fetchStampedDocuments();
    };

    const handleViewAttachments = (rowId: number) => {
        const documentDetail = documentDetailsData.find(detail => detail.stampedDocumentDetailId === rowId);
        if (documentDetail) {
            setSelectedDocumentDetail(documentDetail);
            setViewAttachmentsOpen(true);
        }
    };

    const handleCloseViewAttachments = () => {
        setViewAttachmentsOpen(false);
        setSelectedDocumentDetail(null);
    };

    const handleViewDocument = async (blobAddress: string, fileName?: string) => {
        if (!blobAddress) return;

        // Get file extension to determine how to display
        const extension = (fileName || blobAddress).split('.').pop()?.toLowerCase();
        const isPdf = extension === 'pdf';

        setDocumentLoading(true);
        setDocumentViewerOpen(true);
        setDocumentViewerFileName(fileName || "document.pdf");
        setDocumentViewerBlobAddress(blobAddress);

        try {
            if (isPdf) {

                const response = await fetch(blobAddress, {
                    method: "GET",
                    mode: "cors",
                    credentials: "omit",
                    cache: "no-cache",
                });

                if (!response.ok) {
                    throw new Error(
                        `Failed to download file: ${response.status} ${response.statusText}`,
                    );
                }

                const downloadedBlob = await response.blob();
                const cleanBlob = new Blob([downloadedBlob], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(cleanBlob);
                
                setDocumentViewerUrl(blobUrl);
                setDocumentLoading(false);
            } else {
                // For Office files (DOC, DOCX, XLS, XLSX): Use Microsoft Office Online Viewer
                const officeViewerUrl = `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(blobAddress)}`;
                
                setDocumentViewerUrl(officeViewerUrl);
                setDocumentLoading(false);
            }
        } catch (error) {
            console.error("Error loading document:", error);
            setDocumentLoading(false);
            setDocumentViewerOpen(false);

            Swal.fire({
                title: "Error",
                text: "Failed to load file. Opening in new tab instead.",
                icon: "warning",
                confirmButtonText: "OK",
            }).then(() => {
                window.open(blobAddress, "_blank");
            });
        }
    };

    const handleCloseDocumentViewer = () => {
        if (documentViewerUrl && documentViewerUrl.startsWith('blob:')) {
            URL.revokeObjectURL(documentViewerUrl);
        }
        setDocumentViewerOpen(false);
        setDocumentViewerUrl(null);
        setDocumentViewerFileName(null);
        setDocumentViewerBlobAddress(null);
    };

    const handleDownloadFromViewer = () => {
        if (documentViewerBlobAddress && documentViewerFileName) {
            handleDownloadDocument(documentViewerBlobAddress, documentViewerFileName);
        }
    };

    const handleDownloadDocument = async (blobAddress: string, documentName: string) => {
        try {
            const response = await fetch(blobAddress);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = documentName || 'document';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading document:', error);
            // Fallback to opening in new tab
            window.open(blobAddress, '_blank');
        }
    };

    const handleDownloadRow = async (rowId: number) => {
        const documentDetail = documentDetailsData.find(detail => detail.stampedDocumentDetailId === rowId);
        if (!documentDetail || !documentDetail.documents || documentDetail.documents.length === 0) {
            Swal.fire({
                title: 'No Documents',
                text: 'No documents found for this record.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }

        // If only one document, download directly
        if (documentDetail.documents.length === 1) {
            const doc = documentDetail.documents[0];
            if (doc.blobAddress) {
                window.open(doc.blobAddress, '_blank');
            }
            return;
        }

        // If multiple documents, generate a zip file
        setLoading(true);
        setIsLoading(true);
        try {
            const documentsPayload = documentDetail.documents
                .filter((doc) => doc.documentName && doc.blobAddress)
                .map((doc) => ({
                    documentName: doc.documentName || '',
                    blobAddress: doc.blobAddress || '',
                }));

            if (documentsPayload.length === 0) {
                Swal.fire({
                    title: 'Error',
                    text: 'No valid documents to download.',
                    icon: 'error',
                    confirmButtonText: 'OK',
                });
                setLoading(false);
                setIsLoading(false);
                return;
            }

            const response = await apiClient('/StampedDocument/generateStampedDocumentsZip', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify({
                    documents: documentsPayload,
                }),
            });

            if (response?.status && response?.data) {
                if (response.data.zipUrl) {
                    window.open(response.data.zipUrl, '_blank');
                } else if (response.data.zipBase64) {
                    const byteCharacters = atob(response.data.zipBase64);
                    const byteNumbers = new Array(byteCharacters.length);
                    for (let i = 0; i < byteCharacters.length; i++) {
                        byteNumbers[i] = byteCharacters.charCodeAt(i);
                    }
                    const byteArray = new Uint8Array(byteNumbers);
                    const blob = new Blob([byteArray], { type: 'application/zip' });

                    const fileName = `StampedDocuments_${rowId}_${moment().format('YYYY-MM-DD')}.zip`;
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = fileName;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    Swal.fire({
                        title: 'Warning',
                        text: response?.message || 'Download initiated, but no file data received.',
                        icon: 'warning',
                        confirmButtonText: 'OK',
                    });
                    return;
                }

                Swal.fire({
                    title: 'Success!',
                    text: response?.message || 'Documents downloaded successfully.',
                    icon: 'success',
                    confirmButtonText: 'OK',
                });
            } else {
                throw new Error(response?.message || 'Failed to generate ZIP file.');
            }
        } catch (error: any) {
            console.error('Error downloading documents:', error);
            Swal.fire({
                title: 'Error',
                text: error?.message || 'Failed to download documents. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };


    const handleFilterModelChange = useCallback((newFilterModel: GridFilterModel) => {
        const nextGridItems = newFilterModel.items.filter(
            (i) => !QUEUE_BAR_FILTER_ITEM_IDS.has(String(i.id ?? ''))
        );
        const nextLink = newFilterModel.linkOperator;

        setGridFilterItems((prev) => {
            if (stampedFilterItemsKey(prev) === stampedFilterItemsKey(nextGridItems)) {
                return prev;
            }
            return nextGridItems;
        });
        setFilterLinkOperator((prev) => (prev === nextLink ? prev : nextLink));
        setCurrentPage(0);
    }, []);

    useEffect(() => {
        if (!token) return;
        apiClient('/Partners/getPartners', {
            method: 'GET',
            headers: new Headers({
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
            }),
        })
            .then((responseJson: { data?: StampedDocPartnerOption[] }) => {
                if (responseJson?.data) setQueuePartners(responseJson.data);
            })
            .catch((err) => console.error('Error fetching partners for stamped document filters:', err));
    }, [token]);

    const handleStampedFilterSearch = () => {
        setQueueFilterClientId(draftQueueFilterClientId);
        setQueueFilterStateType(draftQueueFilterStateType);
        setQueueFilterSentBy(draftQueueFilterSentBy.trim());
        setCurrentPage(0);
    };

    const handleStampedFilterClear = () => {
        setDraftQueueFilterClientId(0);
        setDraftQueueFilterStateType('');
        setDraftQueueFilterSentBy('');
        setQueueFilterClientId(0);
        setQueueFilterStateType('');
        setQueueFilterSentBy('');
        setGridFilterItems([]);
        setFilterLinkOperator(undefined);
        setCurrentPage(0);
    };

    const handleStampedFilterRefresh = () => {
        window.location.reload();
    };

    const handleSortModelChange = useCallback((newSortModel: GridSortModel) => {
        if (newSortModel.length > 0) {
            setSortField(newSortModel[0].field);
            setSortDirection(newSortModel[0].sort || 'asc');
        } else {
            setSortField('');
            setSortDirection('desc');
        }
        setCurrentPage(0); // Reset to first page when sort changes
    }, []);

    const fetchStampedDocuments = async () => {
        setLoading(true);
        setIsLoading(true);
        try {
            const apiSortField = (() => {
                const f = sortField || 'sentOn';
                if (f === 'sentBy') return 'userFirstName';
                return f;
            })();

            const requestBody = {
                pageNumber: currentPage + 1, // API uses 1-based indexing
                pageSize: pageSize,
                field: apiSortField,
                sort: sortDirection || 'desc',
                filterModel: normalizeStampedFilterModelForApi(filterModel),
                listScope: selectedTab,
            };

            const response = await apiClient('/StampedDocument/getStampedDocuments', {
                method: 'POST',
                headers: new Headers({
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }),
                body: JSON.stringify(requestBody)
            });

            if (response?.data) {
                const data: StampedDocumentApiResponse = response.data;
                const documentDetails = data.documentDetails || (data as any).documentDetails || [];
                
                // Filter by archive status based on selected tab
                const filteredDocumentDetails = documentDetails.filter((detail: DocumentDetail) => {
                    const isArchived = detail.isArchived || false;
                    if (selectedTab === TabNames.Open) {
                        return !isArchived; // Show non-archived documents
                    } else {
                        return isArchived; // Show archived documents
                    }
                });
                
                const mappedRows: StampedDocumentRow[] = filteredDocumentDetails
                    .filter(detail => detail && detail.stampedDocumentDetailId && typeof detail.stampedDocumentDetailId === 'number')
                    .map((detail: DocumentDetail) => {
                        // Get the sentOn date from the first document, or use the latest one
                        let sentOnDate = '';
                        if (detail.documents && detail.documents.length > 0) {
                            const sortedDocs = [...detail.documents].sort((a, b) => 
                                new Date(b.sentOn).getTime() - new Date(a.sentOn).getTime()
                            );
                            sentOnDate = sortedDocs[0].sentOn ?? '';
                        }
                        
                        return {
                            id: detail.stampedDocumentDetailId,
                            type: detail.documentType || '',
                            client: detail.client || '',
                            partnersId: detail.partnersId || 0,
                            sentOn: sentOnDate,
                            sentBy: detail.userFirstName || '',
                            isArchived: detail.isArchived || false,
                        };
                    })
                    .filter(row => row && row.id && typeof row.id === 'number');
                const validRows = mappedRows.filter(row => 
                    row && 
                    row.id !== undefined && 
                    row.id !== null && 
                    typeof row.id === 'number' &&
                    row.id > 0
                );
                
                setRows(validRows);
                const serverTotal =
                    typeof data.totalCount === 'number' ? data.totalCount : validRows.length;
                setTotalCount(serverTotal);
                setDocumentDetailsData(documentDetails);
            } else {
                setRows([]);
                setTotalCount(0);
            }
        } catch (error) {
            console.error('Error fetching stamped documents:', error);
            Swal.fire({
                title: 'Error',
                text: 'Failed to fetch stamped documents. Please try again.',
                icon: 'error',
                confirmButtonText: 'OK',
            });
            setRows([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchStampedDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, pageSize, sortField, sortDirection, filterFetchKey, selectedTab]);

    const handleDelete = async() => {
        if (selectedRows.length === 0) {
            Swal.fire({
                title: 'No items selected',
                text: "Please select at least one item to delete",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#02A4E3',
                cancelButtonColor: '#d33',
                confirmButtonText: 'OK',
            });
        } else {
            Swal.fire({
                title: 'Are you sure?',
                text: "You won't be able to revert this!",
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#02A4E3',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Delete',
                customClass: {
                    confirmButton: 'btn-radius',
                    cancelButton: 'btn-radius',
                    popup: 'card-radius'
                }
            }).then(async (result) => {
                if (result.isConfirmed) {
                    setLoading(true);
                    // Send stampedDocumentDetailId from selected rows
                    const requestBody = {
                        stampedDocumentDetailId: selectedRows.filter(id => id && typeof id === 'number' && id > 0)
                    };
                    const response = await apiClient('/StampedDocument/deleteStampedDocuments', {
                        method: 'POST',
                        headers: new Headers({
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`,
                        }),
                        body: JSON.stringify(requestBody)
                    });
                    
                    if (response?.status === true || response?.data) {
                        // Clear selected rows and refresh the data after successful deletion
                        setSelectedRows([]);
                        await fetchStampedDocuments();
                        
                        Swal.fire({
                            title: 'Deleted successfully',
                            text: response?.message || "The items have been deleted successfully",
                            icon: 'success',
                            confirmButtonColor: '#02A4E3',
                            confirmButtonText: 'OK',
                        });
                    } else {
                        Swal.fire({
                            title: 'Error',
                            text: response?.message || 'Failed to delete stamped documents. Please try again.',
                            icon: 'error',
                            confirmButtonText: 'OK',
                        });
                    }
                    setLoading(false);
                }
            });
        }
    };

    const markAsArchived = () => {
        // only if all selected rows are unarchived
        if(selectedRows.length > 0 && selectedRows.every(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.isArchived === false)) {
            setLoading(true);
            setIsLoading(true);
            const selectedRowsIds = selectedRows.map(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.stampedDocumentDetailId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these documents to archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move to archive
                    apiClient(`/StampedDocument/markAsArchived?stampedDocumentDetailIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the stamped documents after moving to archive
                        fetchStampedDocuments();
                        Swal.fire({
                            title: 'Documents marked as archived',
                            text: 'The selected documents have been marked as archived',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error marking documents as archived:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                        setIsLoading(false);
                    });
                }
            }).finally(() => {
                setLoading(false);
                // setSelectedRows([]);
                setIsLoading(false);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No documents selected</div>',
                html: '<div style="color: white;">Please select at least one document to mark as archived</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };
    const markAsUnarchived = () => {
        // only if all selected rows are archived
        if(selectedRows.length > 0 && selectedRows.every(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.isArchived === true)) {
            setLoading(true);
            setIsLoading(true);
            const selectedRowsIds = selectedRows.map(row => documentDetailsData.find(detail => detail.stampedDocumentDetailId === row)?.stampedDocumentDetailId);
            Swal.fire({
                title: '<div style="color: white;">Are you sure?</div>',
                html: '<div style="color: white;">You want to move these documents from archive?</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                confirmButtonColor: GreenBtnColor,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                confirmButtonText: 'Yes',
            }).then((result) => {
                if(result.isConfirmed) {
                    // call api to move from archive
                    apiClient(`/StampedDocument/markAsUnarchived?stampedDocumentDetailIdList=${selectedRowsIds.join(',')}`, {
                        method: 'POST',
                        headers: new Headers({
                            Authorization: `Bearer ${token}`,
                        }),
                    }).then((responseJson) => {
                        if (!responseJson) return;
                        // refresh the stamped documents after moving from archive
                        fetchStampedDocuments();
                        Swal.fire({
                            title: 'Documents moved from archive',
                            text: 'The selected documents have been moved from archive',
                            icon: 'success',
                        });
                    }).catch(error => {
                        console.error('Error moving documents from archive:', error);
                    }).finally(() => {
                        setLoading(false);
                        setSelectedRows([]);
                        setIsLoading(false);
                    });
                }
            }).finally(() => {
                setLoading(false);
                setSelectedRows([]);
                setIsLoading(false);
            });
        } else {
            Swal.fire({
                title: '<div style="color: white;">No documents selected</div>',
                html: '<div style="color: white;">Please select at least one archived document to move from archive</div>',
                background: PrimaryBlueColor,
                showCancelButton: true,
                cancelButtonColor: OrangeBtnColor,
                reverseButtons: true,
                showConfirmButton: false,
            });
        }
    };


    const handleExport = () => {
        const rowsToExport = selectedRows.length > 0 
            ? rows.filter(row => selectedRows.includes(row.id))
            : rows;

        if (rowsToExport.length === 0) {
            Swal.fire({
                title: 'No data to export',
                text: selectedRows.length > 0 
                    ? 'No selected rows to export.'
                    : 'There are no stamped documents to export.',
                icon: 'warning',
                confirmButtonText: 'OK',
            });
            return;
        }
        exportToExcel(rowsToExport);
    };

    const exportToExcel = (rowsToExport: StampedDocumentRow[] = rows) => {
        const headers = ['State', 'Client', 'Sent On', 'Sent By'];
        
        const dataRows = rowsToExport.map(row => [
            documentTypeToStateCode(row.type || '') || '',
            row.client || '',
            formatUtcToLocal(row.sentOn, 'MM/DD/YYYY'),
            row.sentBy || '',
        ]);
        const exportData = [headers, ...dataRows];
        const timeStamp = moment().format('YYYY.M.D');
        const rowCount = rowsToExport.length;
        const filename = `StampedDocuments_${timeStamp}${selectedRows.length > 0 ? `_${rowCount}Selected` : ''}.xlsx`;

        writeXlsxFile(exportData, filename);
    };

    function CustomToolbar() {
        return (
            <GridToolbarContainer>
                <GridToolbarFilterButton className="me-3 sky-blue" />
                <GridToolbarColumnsButton className="me-3 sky-blue" />
                <GridToolbarDensitySelector className="me-3 sky-blue" />
            </GridToolbarContainer>
        );
    }

    function EditToolbar() {
        const [toolbarMenuAnchor, setToolbarMenuAnchor] = useState<null | HTMLElement>(null);
        const openToolbarMenu = Boolean(toolbarMenuAnchor);

        const handleToolbarMenuClose = () => setToolbarMenuAnchor(null);
        const handleToolbarAction = (action: 'filter' | 'columns' | 'exportCsv' | 'exportPrint') => {
            handleToolbarMenuClose();
            const api = sendStampedDocumentGridApiRef.current;
            if (!api) return;
            if (action === 'filter') api.showFilterPanel();
            else if (action === 'columns') (api as any).showPreferences('columns');
            else if (action === 'exportCsv') api.exportDataAsCsv();
            else if (action === 'exportPrint') api.exportDataAsPrint();
        };

        return (<>
            <div className="p-2 d-flex justify-content-end datagrid-butons">
                {!IsUserClient && (
                    <>
                        {/* Tabs: desktop only (md and up) */}
                        <Tabs
                            value={selectedTab}
                            onChange={(_, newValue) => handleTabChange(newValue)}
                            aria-label="Stamped document tabs"
                            sx={{
                                display: { xs: 'none', md: 'flex' },
                                minHeight: 48,
                                marginRight: 2,
                                '& .MuiButtonBase-root': { minHeight: '0px' },
                                '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.9375rem' },
                                '& .Mui-selected': { color: '#2099d2' },
                                '& .MuiTabs-indicator': { backgroundColor: '#2099d2', height: 3, borderRadius: '3px 3px 0 0' },
                            }}
                        >
                            <Tab label="Open" value={TabNames.Open} icon={<FaPaperPlane fontSize="small" />} iconPosition="start" />
                            <Tab label="Archived" value={TabNames.Archive} icon={<FaArchive fontSize="small" />} iconPosition="start" />
                        </Tabs>
                        {/* Select option for mobile/tablet view */}
                        <FormControl
                            size="small"
                            sx={{
                                display: { xs: 'block', md: 'none' },
                                minWidth: 160,
                                marginRight: 2,
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                                '& .MuiSelect-select': { fontWeight: 600, color: '#2099d2', py: 0.9 },
                            }}
                        >
                            <Select
                                value={selectedTab}
                                onChange={(e) => handleTabChange(e.target.value as TabNames)}
                                displayEmpty
                                sx={{ textTransform: 'none', fontSize: '0.9375rem' }}
                            >
                                <MenuItem value={TabNames.Open}>Sent Documents</MenuItem>
                                <MenuItem value={TabNames.Archive}>Archived</MenuItem>
                            </Select>
                        </FormControl>
                    </>
                )}
                
                <Box className='ms-auto' sx={{ display: 'flex', alignItems: 'center' }}>
                    {isTabletOrDown ? (
                        <>
                            <IconButton
                                aria-label="Table options"
                                aria-controls={openToolbarMenu ? 'table-options-menu' : undefined}
                                aria-haspopup="true"
                                aria-expanded={openToolbarMenu ? 'true' : undefined}
                                onClick={(e) => setToolbarMenuAnchor(e.currentTarget)}
                                sx={{ color: '#2099d2' }}
                            >
                                <MoreVertOutlinedIcon />
                            </IconButton>
                            <Menu
                                id="table-options-menu"
                                anchorEl={toolbarMenuAnchor}
                                open={openToolbarMenu}
                                onClose={handleToolbarMenuClose}
                                MenuListProps={{ 'aria-labelledby': 'table-options-button' }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <MenuItem onClick={() => handleToolbarAction('filter')}>Filter</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('columns')}>Columns</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportCsv')}>Download as CSV</MenuItem>
                                <MenuItem onClick={() => handleToolbarAction('exportPrint')}>Print</MenuItem>
                            </Menu>
                        </>
                    ) : (
                        <CustomToolbar />
                    )}
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={handleExport}
                        startIcon={<FaDownload fontSize="small" />}
                        sx={{
                            alignSelf: 'center',
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: '#2099d2',
                            '&:hover': { backgroundColor: '#176990' },
                            px: 2,
                        }}
                    >
                        Export
                    </Button>
                </Box>
            </div>
        </>);
    }

    useEffect(() => {
        if (documentDetailsData.length > 0) {
            setDataLoaded(true);
        }
    }, [documentDetailsData]);

    useEffect(() => {
        if (dataLoaded) {
            setIsLoading(false);
        }
    }, [dataLoaded]);

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <div style={{ position: "fixed", top: "0", left: "0", zIndex: 99999, width: "100%", pointerEvents: "none" }}>
                {loading && <LinearProgress color="info" sx={{ backgroundColor: "#132D3D" }} />}
            </div>
            <Box sx={{ width: "100%" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e2e8f0",
                        backgroundColor: "#fafbfc",
                        minHeight: 56,
                        marginLeft: { xs: '-24px', md: '0px' },
                        marginRight: { xs: '-24px', md: '0px' },
                    }}
                >
                <h4 className="mb-0 py-2" style={{ fontWeight: 600, color: "#0f172a" }}>I Want To... Send a Stamped Document</h4>
                <Box sx={{ width: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                    {!IsUserClient && (<>
                    <Button onClick={handleNewSubmission}
                        variant="contained"
                        size="medium"
                        startIcon={<FaPlus fontSize="small" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: LightBlueBtnColor,
                            '&:hover': { backgroundColor: '#176990' },
                            px: 2,
                        }}
                    >
                        New Submission
                    </Button>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={handleDelete}
                        disabled={selectedRows.length === 0}
                        startIcon={<FaTrash fontSize="small" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: '#dc2626',
                            '&:hover': { backgroundColor: '#b91c1c' },
                            px: 2,
                        }}
                    >
                        Delete
                    </Button>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={selectedTab === TabNames.Archive ? markAsUnarchived : markAsArchived}
                        disabled={selectedRows.length === 0}
                        startIcon={<FaArchive fontSize="small" />}
                        sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            backgroundColor: '#64748b',
                            '&:hover': { backgroundColor: '#475569' },
                            px: 2,
                        }}
                    >
                        {selectedTab === TabNames.Archive ? 'Unarchive' : 'Archive'}
                    </Button>
                    </>)}
                </Box>
            </Box>
            </Box>
            <Box sx={{ width: '100%', py: 2, px: 2 }}>
                <Grid container spacing={2} className="filter-grid" display="flex">
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                    <Autocomplete
                                        sx={{
                                            marginTop: '-4px !important',
                                            '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                        }}
                                        options={stampedFilterPartnerOptions}
                                        getOptionLabel={(option) => option.customerName}
                                        isOptionEqualToValue={(a, b) => Number(a.partnersId) === Number(b.partnersId)}
                                        value={stampedFilterPartnerOptions.find((p) => Number(p.partnersId) === draftQueueFilterClientId) ?? null}
                                        onChange={(_, v) => setDraftQueueFilterClientId(v ? Number(v.partnersId) : 0)}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="Client"
                                                size="small"
                                                sx={{
                                                    '& .MuiInputBase-root': { marginTop: '5px !important' },
                                                    '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                                    '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                                    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                                }}
                                            />
                                        )}
                                    />
                                    </FormControl>
                                </Grid>
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <FormControl fullWidth>
                                    <Autocomplete
                                        sx={{
                                            marginTop: '-4px !important',
                                            '& .MuiAutocomplete-input': { padding: '0px 4px 0px 5px !important' },
                                        }}
                                        options={stampedFilterStateOptions}
                                        getOptionLabel={(option) => option}
                                        isOptionEqualToValue={(a, b) => a === b}
                                        value={draftQueueFilterStateType || null}
                                        onChange={(_, v) => setDraftQueueFilterStateType(v ?? '')}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label="State"
                                                size="small"
                                                sx={{
                                                    '& .MuiInputBase-root': { marginTop: '5px !important' },
                                                    '& .MuiOutlinedInput-root': { padding: '4.5px 9px' },
                                                    '& .MuiInputLabel-root': { transform: 'translate(14px, 12px) scale(1)' },
                                                    '& .MuiInputLabel-shrink': { transform: 'translate(14px, -9px) scale(0.75)' },
                                                }}
                                            />
                                        )}
                                    />
                                    </FormControl>
                                </Grid>
                                <Grid item xl={2} lg={4} sm={6} xs={12}>
                                    <TextField
                                    size="small"
                                    label="Sent By"
                                    value={draftQueueFilterSentBy}
                                    onChange={(e) => setDraftQueueFilterSentBy(e.target.value)}
                                    placeholder="Contains…"
                                    sx={{
                                        minWidth: { xs: '100%', sm: 180 },
                                        flex: { sm: '0 1 220px' },
                                        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#c4c4c4' },
                                        '& .MuiInputBase-root': { marginTop: '1px !important' },
                                        '& .MuiInputBase-input': { padding: '6px 14px !important' },
                                    }}
                                    fullWidth
                                    inputProps={{ 'aria-label': 'Filter by sent by' }}
                                />
                                </Grid>
                                <Grid item xs={12} sm="auto">
                                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    <Button
                                        variant="contained"
                                        size="medium"
                                        onClick={handleStampedFilterSearch}
                                        startIcon={<Search />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            backgroundColor: '#2099d2',
                                            '&:hover': { backgroundColor: '#176990' },
                                        }}
                                    >
                                        Search
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        onClick={handleStampedFilterClear}
                                        startIcon={<Clear />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: '#e2e8f0',
                                            color: '#475569',
                                            '&:hover': { borderColor: '#2099d2', color: '#2099d2' },
                                        }}
                                    >
                                        Clear
                                    </Button>
                                    <Button
                                        variant="outlined"
                                        size="medium"
                                        onClick={handleStampedFilterRefresh}
                                        startIcon={<Refresh />}
                                        sx={{
                                            textTransform: 'none',
                                            fontWeight: 600,
                                            borderColor: '#e2e8f0',
                                            color: '#475569',
                                            '&:hover': {
                                                borderColor: '#2099d2',
                                                backgroundColor: '#2099d214',
                                                color: '#2099d2',
                                            },
                                        }}
                                    >
                                        Refresh
                                    </Button>
                                    </Box>
                                </Grid>
                </Grid>
            </Box>
            <Box sx={{ width: "100%" }}>
                        <div
                            style={{
                                height: "calc(100vh - 300px)",
                                display: "flex",
                                flexDirection: "column",
                                width: "100%",
                                borderRadius: "12px",
                                overflow: "hidden",
                                boxShadow: "0 4px 24px rgba(15, 23, 42, 0.08), 0 1px 3px rgba(15, 23, 42, 0.06)",
                                border: "1px solid #e2e8f0",
                                backgroundColor: "#ffffff",
                                position: "relative"
                            }}
                        >
                            {loading && (
                                <Box
                                    position="absolute"
                                    top={0}
                                    left={0}
                                    right={0}
                                    bottom={0}
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                        backgroundColor: "rgba(250, 251, 252, 0.85)",
                                        zIndex: 10,
                                        border: "1px solid #e2e8f0",
                                        borderRadius: 1,
                                    }}
                                >
                                    <Box sx={{ width: "100%", maxWidth: 320 }}>
                                        <LinearProgress color="info" sx={{ backgroundColor: "#e2e8f0", "& .MuiLinearProgress-bar": { backgroundColor: "#132D3D" } }} />
                                        <Box sx={{ mt: 1.5, textAlign: "center" }}>
                                            <span style={{ fontSize: "14px", fontWeight: 600, color: "#475569" }}>Loading...</span>
                                        </Box>
                                    </Box>
                                </Box>
                            )}
                            <div style={{ flex: '1 1 0%', overflow: "auto" }}>
                                <DataGridPro
                                    apiRef={sendStampedDocumentGridApiRef}
                                    rows={rows}
                                    columns={columns}
                                    page={currentPage}
                                    pageSize={pageSize}
                                    loading={isLoading}
                                    pagination
                                    rowCount={totalCount}
                                    paginationMode="server"
                                    sortingMode="server"
                                    filterMode="server"
                                    filterModel={filterModel}
                                    onFilterModelChange={handleFilterModelChange}
                                    onSortModelChange={handleSortModelChange}
                                    getRowId={(row) => row.id}
                                    onPageChange={(newPage) => setCurrentPage(newPage)}
                                    onPageSizeChange={(newPageSize) => {
                                        setPageSize(newPageSize);
                                        setCurrentPage(0);
                                    }}
                                    rowsPerPageOptions={[25, 50, 100]}
                                    checkboxSelection={true}
                                    onSelectionModelChange={(newSelectionModel) => {
                                        setSelectedRows(newSelectionModel as number[]);
                                    }}
                                    components={{
                                        Toolbar: EditToolbar,
                                    }}
                                    sx={{
                                        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                        border: 'none',
                                        fontSize: '14px',
                                        color: '#475569',
                                        backgroundColor: '#ffffff',
                                        "& .MuiDataGrid-columnHeaderTitle": {
                                            whiteSpace: 'normal',
                                            lineHeight: 1.35,
                                            fontWeight: 600,
                                            fontSize: '16px',
                                            letterSpacing: '0.05em',
                                            color: '#162E3C',
                                        },
                                        '& .MuiDataGrid-columnHeaders': {
                                            backgroundColor: '#f8fafc',
                                            borderTop: '2px solid #e2e8f0',
                                            borderBottom: '2px solid #e2e8f0',
                                            minHeight: '52px !important',
                                            maxHeight: '52px !important',
                                        },
                                        '& .MuiDataGrid-columnHeader': {
                                            '&:focus, &:focus-within': { outline: 'none' },
                                        },
                                        '& .MuiDataGrid-row': {
                                            transition: 'background-color 0.15s ease',
                                            '&:nth-of-type(even)': {
                                                backgroundColor: '#fafbfc',
                                            },
                                            '&:hover': {
                                                backgroundColor: '#f1f5f9 !important',
                                            },
                                            '&.Mui-selected': {
                                                backgroundColor: '#ebf5fa !important',
                                                '&:hover': {
                                                    backgroundColor: '#dbeef7 !important',
                                                },
                                            },
                                        },
                                        '& .MuiDataGrid-cell': {
                                            borderBottom: '1px solid #f1f5f9',
                                            fontSize: '14px',
                                            color: '#334155',
                                            '&:focus': { outline: 'none' },
                                        },
                                        ".MuiDataGrid-virtualScroller": {
                                            border: 'none',
                                        },
                                        "& .MuiDataGrid-main": {
                                            border: 'none',
                                        },
                                        "& .MuiDataGrid-footerContainer": {
                                            borderTop: '1px solid #e2e8f0',
                                            backgroundColor: '#f8fafc',
                                            minHeight: 56,
                                        },
                                        "& .MuiTablePagination-root": {
                                            color: '#64748b',
                                            fontSize: '13px',
                                        },
                                        "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                            marginBottom: 0,
                                        },
                                        "& .MuiTablePagination-select": {
                                            fontSize: '13px',
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                        },
                                        "& .MuiIconButton-root": {
                                            color: '#64748b',
                                            "&:hover": {
                                                backgroundColor: '#f1f5f9',
                                                color: '#475569',
                                            },
                                        },
                                        "& .MuiCheckbox-root": {
                                            color: '#94a3b8',
                                            "&.Mui-checked": {
                                                color: '#2099d2',
                                            },
                                        },
                                    }}
                                />
                            </div>
                        </div>
            </Box>
            <AddStampedDocument 
                Open={addStampedDocumentOpen} 
                handleClose={handleCloseAddStampedDocument}
                onSuccess={handleStampedDocumentSuccess}
                onLoadingChange={setLoading}
            />
            <Dialog
                open={viewAttachmentsOpen}
                onClose={handleCloseViewAttachments}
                maxWidth="lg"
                fullWidth
            >
                <DialogTitle
                    sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 1,
                        py: 2,
                        px: 3,
                        background: 'linear-gradient(135deg, #0d4a63 0%, #15607a 100%)',
                        color: '#fff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <DescriptionOutlinedIcon sx={{ fontSize: 28, opacity: 0.95 }} />
                        <Typography component="span" variant="h6" sx={{ fontWeight: 600, letterSpacing: '0.02em' }}>Document Attachments</Typography>
                    </Box>
                    <IconButton onClick={handleCloseViewAttachments} sx={{ color: "#ffffff" }} aria-label="close">
                        <CloseOutlinedIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent sx={{ backgroundColor: '#ffffff', padding: '16px !important', margin: 0 }}>
                    {/* <Typography 
                        variant="body1" 
                        sx={{ 
                            fontWeight: 'bold', 
                            marginBottom: '16px',
                            fontFamily: "'Proxima Nova', sans-serif",
                        }}
                    >
                        Document Attachments:
                    </Typography> */}
                    {selectedDocumentDetail && selectedDocumentDetail.documents && selectedDocumentDetail.documents.length > 0 ? (
                        (() => {
                            const documentRows = selectedDocumentDetail.documents.map((doc, index) => ({
                                id: doc.stampedDocumentId || index,
                                type: selectedDocumentDetail.documentType || 'N/A',
                                documentName: doc.documentName || 'N/A',
                                blobAddress: doc.blobAddress || '',
                            }));

                            const columns: GridColDef[] = [
                                {
                                    field: 'download',
                                    headerName: 'Download',
                                    width: 180,
                                    align: 'left',
                                    headerAlign: 'left',
                                    sortable: false,
                                    filterable: false,
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                justifyContent: 'left',
                                                alignItems: 'left',
                                                width: '100%',
                                            }}
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => handleViewDocument(params.row.blobAddress, params.row.documentName)}
                                                disabled={!params.row.blobAddress}
                                                title="View"
                                                sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                                            >
                                                <Tooltip title="View" arrow={true} placement="top">
                                                    <RemoveRedEyeOutlinedIcon fontSize="small" />
                                                </Tooltip>
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => handleDownloadDocument(params.row.blobAddress, params.row.documentName)}
                                                disabled={!params.row.blobAddress}
                                                title="Download"
                                                sx={{ color: 'rgba(255,255,255,0.9)', '&:hover': { color: '#fff', backgroundColor: 'rgba(32,153,210,0.15) !important' } }}
                                            >
                                                <Tooltip title="Download" arrow={true} placement="top">
                                                    <FileDownloadOutlinedIcon fontSize="small" />
                                                </Tooltip>
                                            </IconButton>
                                        </Box>
                                    ),
                                },
                                {
                                    field: 'type',
                                    headerName: 'State',
                                    width: 250,
                                    align: 'left',
                                    headerAlign: 'left',
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'left',
                                                alignItems: 'left',
                                                width: '100%',
                                            }}
                                        >
                                            {documentTypeToStateCode(String(params.value || '')) || '—'}
                                        </Box>
                                    ),
                                },
                                {
                                    field: 'documentName',
                                    headerName: 'Document Name',
                                    flex: 1,
                                    minWidth: 300,
                                    align: 'left',
                                    headerAlign: 'left',
                                    renderCell: (params) => (
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                justifyContent: 'left',
                                                alignItems: 'left',
                                                width: '100%',
                                            }}
                                        >
                                            {params.value}
                                        </Box>
                                    ),
                                },
                            ];

                            return (
                                <Paper
                                    sx={{
                                        width: '100%',
                                        border: 'none',
                                        overflow: 'hidden',
                                        boxShadow: 'none',
                                        borderRadius: 0,
                                        margin: 0,
                                        padding: 0,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            height: Math.max(400, 56 + documentRows.length * 52 + 56),
                                            width: '100%',
                                            margin: 0,
                                            padding: 0,
                                        }}
                                    >
                                        <DataGridPro
                                            rows={documentRows}
                                            columns={columns}
                                            pagination
                                            pageSize={5}
                                            rowsPerPageOptions={[5, 10, 25]}
                                            disableColumnMenu
                                            disableSelectionOnClick
                                            getRowHeight={() => 52}
                                            sx={{
                                                fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                                                border: 'none',
                                                fontSize: '14px',
                                                color: '#475569',
                                                backgroundColor: '#ffffff',
                                                "& .MuiDataGridPro-columnHeaderTitle": {
                                                    whiteSpace: "normal",
                                                    lineHeight: 1.35,
                                                    fontWeight: 600,
                                                    fontSize: "12px",
                                                    letterSpacing: "0.05em",
                                                    textTransform: "uppercase",
                                                    color: "#64748b",
                                                },
                                                "& .MuiDataGrid-columnHeaders": {
                                                    backgroundColor: "#f8fafc",
                                                    borderBottom: "2px solid #e2e8f0",
                                                    borderTop: "2px solid #e2e8f0",
                                                    minHeight: "52px !important",
                                                    maxHeight: "52px !important",
                                                },
                                                '& .MuiDataGrid-columnHeader': {
                                                    '&:focus, &:focus-within': { outline: 'none' },
                                                },
                                                '& .MuiDataGrid-row': {
                                                    transition: 'background-color 0.15s ease',
                                                    '&:nth-of-type(even)': {
                                                        backgroundColor: '#fafbfc',
                                                    },
                                                    '&:hover': {
                                                        backgroundColor: '#f1f5f9 !important',
                                                    },
                                                    '&.Mui-selected': {
                                                        backgroundColor: '#ebf5fa !important',
                                                        '&:hover': {
                                                            backgroundColor: '#dbeef7 !important',
                                                        },
                                                    },
                                                },
                                                '& .MuiDataGrid-cell': {
                                                    borderBottom: '1px solid #f1f5f9',
                                                    fontSize: '14px',
                                                    color: '#334155',
                                                    '&:focus': { outline: 'none' },
                                                },
                                                ".MuiDataGrid-virtualScroller": {
                                                    border: 'none',
                                                },
                                                "& .MuiDataGrid-main": {
                                                    border: 'none',
                                                },
                                                "& .MuiDataGrid-footerContainer": {
                                                    borderTop: '1px solid #e2e8f0',
                                                    backgroundColor: '#f8fafc',
                                                    minHeight: 56,
                                                },
                                                "& .MuiTablePagination-root": {
                                                    color: '#64748b',
                                                    fontSize: '13px',
                                                },
                                                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                                                    marginBottom: 0,
                                                },
                                                "& .MuiTablePagination-select": {
                                                    fontSize: '13px',
                                                    borderRadius: '6px',
                                                    border: '1px solid #e2e8f0',
                                                },
                                                "& .MuiIconButton-root": {
                                                    color: '#64748b',
                                                    "&:hover": {
                                                        backgroundColor: '#f1f5f9',
                                                        color: '#475569',
                                                    },
                                                },
                                            }}
                                        />
                                    </Box>
                                </Paper>
                            );
                        })()
                    ) : (
                        <Typography sx={{ 
                            color: '#333', 
                            textAlign: 'center',
                            fontFamily: "'Proxima Nova', sans-serif",
                            padding: '20px'
                        }}>
                            No attachments found
                        </Typography>
                    )}
                </DialogContent>
            </Dialog>

            {/* Document Viewer Dialog */}
            <Dialog
                open={documentViewerOpen}
                onClose={handleCloseDocumentViewer}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    style: {
                        maxHeight: "90vh",
                    },
                }}
            >
                <DialogTitle className="card-title">
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            width: "100%",
                        }}
                    >
                        <Box
                            sx={{
                                flex: 1,
                                fontWeight: "medium",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                paddingRight: "16px",
                            }}
                        >
                            {documentViewerFileName || ""}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <IconButton
                                onClick={handleDownloadFromViewer}
                                sx={{
                                    color: "#1976D2",
                                    "&:hover": {
                                        backgroundColor: "rgba(25, 118, 210, 0.08)",
                                    },
                                }}
                                aria-label="download"
                                title="Download"
                            >
                                <CloudDownloadIcon />
                            </IconButton>
                            <IconButton
                                onClick={handleCloseDocumentViewer}
                                sx={{
                                    color: "#666",
                                    "&:hover": {
                                        backgroundColor: "rgba(0, 0, 0, 0.04)",
                                        color: "#000",
                                    },
                                }}
                                aria-label="close"
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent
                    dividers
                    sx={{ padding: 0, height: "80vh", position: "relative" }}
                >
                    {documentLoading ? (
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                height: "100%",
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    ) : documentViewerUrl ? (
                        <Box sx={{ width: "100%", height: "100%" }}>
                            <iframe
                                src={documentViewerUrl}
                                style={{
                                    width: "100%",
                                    height: "100%",
                                    border: "none",
                                }}
                                title="Document Viewer"
                            />
                        </Box>
                    ) : null}
                </DialogContent>
            </Dialog>
        </LocalizationProvider>
    );
};

export default SendStampedDocument;
